/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {
  Directive,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  Renderer2,
  inject,
  effect,
  DestroyRef
} from '@angular/core';
import { BsAutocompleteComponent } from './bs-autocomplete.component';

/**
 * Directive to make bs-autocomplete compatible with PrimeNG 18 <p-floatLabel>.
 *
 * Usage:
 * ```html
 * <p-floatLabel>
 *   <bs-autocomplete bsFloatLabelCompat inputId="myInput" ...></bs-autocomplete>
 *   <label for="myInput">My Label</label>
 * </p-floatLabel>
 * ```
 */
@Directive({
  selector: 'bs-autocomplete[bsFloatLabelCompat]',
  standalone: true
})
export class BsFloatLabelCompatDirective implements AfterViewInit, OnDestroy {
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly ac = inject(BsAutocompleteComponent, { host: true, optional: true });
  private readonly destroyRef = inject(DestroyRef);

  private inputEl: HTMLInputElement | null = null;
  private abortController = new AbortController();

  constructor() {
    // Watch the component's text and value signals to update filled state
    // This handles initial values set via writeValue
    if (this.ac) {
      effect(() => {
        const text = this.ac!.text();
        const value = this.ac!.value();
        // Update filled state whenever text or value changes
        this.updateFilledStateFromSignal(text, value);
      });
    }
  }

  /**
   * Update filled state based on component signals.
   * Called from the effect when text/value changes.
   */
  private updateFilledStateFromSignal(text: string, value: unknown): void {
    const host = this.hostEl.nativeElement;
    const hasValue = text.length > 0 || value != null;

    if (hasValue) {
      this.renderer.addClass(host, this.filledClass);
    } else {
      this.renderer.removeClass(host, this.filledClass);
    }
  }

  /** CSS class added to host when input is focused */
  private readonly focusedClass = 'p-inputwrapper-focus';
  /** CSS class added to host when input has value */
  private readonly filledClass = 'p-inputwrapper-filled';

  ngAfterViewInit(): void {
    this.inputEl = this.findInputElement();

    if (!this.inputEl) {
      console.warn('[BsFloatLabelCompatDirective] Could not find input element in bs-autocomplete');
      return;
    }

    this.decorateInput();
    this.setupFocusForwarding();
    this.setupFocusListeners();
    this.updateFilledState();
    this.fixLabelZIndex();
  }

  /**
   * Fix label z-index so it always appears above the autocomplete input
   */
  private fixLabelZIndex(): void {
    const floatLabel = this.hostEl.nativeElement.closest('p-floatlabel');
    if (!floatLabel) return;

    const label = floatLabel.querySelector('label');
    if (label) {
      label.style.zIndex = '1';
    }
  }

  ngOnDestroy(): void {
    this.abortController.abort();
  }

  /**
   * Find the internal input element.
   * Priority: component's inputElRef > DOM query fallback
   */
  private findInputElement(): HTMLInputElement | null {
    // Try to get from component's ViewChild (signal-based)
    const inputRef = this.ac?.inputElRef();
    if (inputRef?.nativeElement) {
      return inputRef.nativeElement;
    }

    // Fallback: query the DOM
    return this.hostEl.nativeElement.querySelector('input');
  }

  /**
   * Add PrimeNG classes to the input for FloatLabel compatibility
   */
  private decorateInput(): void {
    if (!this.inputEl) return;

    this.renderer.addClass(this.inputEl, 'p-inputtext');
    this.renderer.addClass(this.inputEl, 'p-component');
  }

  /**
   * Setup mousedown handler on host to forward focus to input.
   * Guards against stealing focus from interactive elements.
   */
  private setupFocusForwarding(): void {
    const host = this.hostEl.nativeElement;
    const signal = this.abortController.signal;

    host.addEventListener('mousedown', (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Don't steal focus from interactive elements
      if (this.isInteractiveElement(target)) {
        return;
      }

      // Don't focus if clicking on the input itself
      if (target === this.inputEl) {
        return;
      }

      // Prevent default to avoid any focus change, then focus input
      event.preventDefault();
      this.inputEl?.focus();
    }, { signal });
  }

  /**
   * Check if an element is interactive and should handle its own focus.
   */
  private isInteractiveElement(el: HTMLElement): boolean {
    // Check for explicit interactive marker
    if (el.hasAttribute('data-bs-ac-interactive')) {
      return true;
    }

    // Check element type
    const tagName = el.tagName.toLowerCase();
    const interactiveTags = ['button', 'a', 'input', 'textarea', 'select'];
    if (interactiveTags.includes(tagName)) {
      return true;
    }

    // Check for role="button" or tabindex
    if (el.getAttribute('role') === 'button' || el.hasAttribute('tabindex')) {
      return true;
    }

    // Check if any ancestor (up to host) is interactive
    let current = el.parentElement;
    while (current && current !== this.hostEl.nativeElement) {
      if (this.isInteractiveElement(current)) {
        return true;
      }
      current = current.parentElement;
    }

    return false;
  }

  /**
   * Setup focus/blur listeners on the input to manage host classes.
   */
  private setupFocusListeners(): void {
    if (!this.inputEl) return;

    const signal = this.abortController.signal;
    const host = this.hostEl.nativeElement;

    // Focus in - add focused class
    this.inputEl.addEventListener('focusin', () => {
      this.renderer.addClass(host, this.focusedClass);
    }, { signal });

    // Focus out - remove focused class and update filled state
    this.inputEl.addEventListener('focusout', () => {
      this.renderer.removeClass(host, this.focusedClass);
      this.updateFilledState();
    }, { signal });

    // Input event - update filled state as user types
    this.inputEl.addEventListener('input', () => {
      this.updateFilledState();
    }, { signal });
  }

  /**
   * Update the filled class based on whether the input has a value.
   * This keeps the label floated when there's content.
   */
  private updateFilledState(): void {
    if (!this.inputEl) return;

    const host = this.hostEl.nativeElement;
    const hasValue = this.inputEl.value.length > 0;

    if (hasValue) {
      this.renderer.addClass(host, this.filledClass);
    } else {
      this.renderer.removeClass(host, this.filledClass);
    }
  }
}
